import React from 'react';

import { Check, ChevronLeft } from 'lucide-react';

import RangeSlider from '../RangeSlider';

import type { PricingConfiguration } from '../../../types';

interface PassThroughCostsStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onSave: () => void;
	onPrevious: () => void;
	isLoading: boolean;
	isEditing?: boolean;
	hideNavigation?: boolean;
}

const PassThroughCostsStep: React.FC<PassThroughCostsStepProps> = ({
	configuration,
	onUpdate,
	onSave,
	onPrevious,
	isLoading,
	isEditing = false,
	hideNavigation = false,
}) => {
	// Helper function to check if a rate type is selected
	const hasRateType = (rateType: string) => {
		const selectedTypes = configuration.selected_rate_types || [
			configuration.rate_plan_type,
		];
		return selectedTypes?.includes(rateType) || false;
	};
	const handleAssessmentRateChange = (useStandard: boolean) => {
		onUpdate({ use_standard_assessment_rates: useStandard });

		// If user selects custom assessment rates, we need to update the API structure
		if (!useStandard) {
			// This will be handled when they set specific values
			// For now, just update the flag
		}
	};

	const handleCustomAssessmentRateChange = (value: number) => {
		onUpdate({
			custom_assessment_rate: value,
			use_standard_assessment_rates: false,
		});
	};

	const handleAmexRateChange = (useBundled: boolean) => {
		onUpdate({ use_bundled_amex_rate: useBundled });
	};

	const handleBundledAmexRateChange = (rate: number) => {
		onUpdate({
			bundled_amex_rate: rate,
			use_bundled_amex_rate: true,
		});
	};

	// Check if multiple rate type categories are selected (need section headers)
	const hasMultipleSections =
		hasRateType('FLAT_TIERED') &&
		(hasRateType('IC_PLUS') || hasRateType('TIERED'));

	return (
		<div className="space-y-8">
			<div className="text-center mb-8">
				<h3 className="text-2xl text-gray-900 mb-2">
					Pass-Through Costs Configuration
				</h3>
				<p className="text-gray-600">
					Configure how you handle Card Brand fees and assessment rates in your
					pricing
				</p>
			</div>

			{/* FLAT_TIERED: Card Brand Pass-Through Toggle */}
			{hasRateType('FLAT_TIERED') && (
				<div className="rounded-lg border border-gray-200 p-6 bg-white">
					{/* Section Header - only show when multiple sections */}
					{hasMultipleSections && (
						<div className="mb-6">
							<h4 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
								<span className="w-3 h-3 bg-purple-500 rounded-full mr-3"></span>
								Flat Tiered Pricing
							</h4>
							<p className="text-gray-600 text-sm">
								Configure Card Brand fee handling for transaction-level
								statements (Stripe, Square)
							</p>
						</div>
					)}

					<div className="space-y-4">
						<h4 className="text-lg font-medium text-gray-900">
							Do you want to pass through Card Brand fees to your clients?
						</h4>

						{/* All-Inclusive Pricing (Recommended) */}
						<label
							className={`flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
								!configuration.pass_through_card_brand_fees &&
								!configuration.pass_through_domestic_card_brand_fees &&
								!configuration.pass_through_international_card_brand_fees
									? 'bg-purple-50 border-purple-500'
									: 'bg-white border-gray-200'
							}`}
						>
							<input
								type="radio"
								name="card_brand_fees"
								value="all_inclusive"
								checked={
									!configuration.pass_through_card_brand_fees &&
									!configuration.pass_through_domestic_card_brand_fees &&
									!configuration.pass_through_international_card_brand_fees
								}
								onChange={() =>
									onUpdate({
										pass_through_card_brand_fees: false,
										pass_through_domestic_card_brand_fees: false,
										pass_through_international_card_brand_fees: false,
									})
								}
								className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<div className="flex items-center">
									<span className="font-semibold text-gray-900">
										All-Inclusive Pricing
									</span>
									<span className="ml-3 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
										Recommended
									</span>
								</div>
								<p className="text-gray-600 mt-2">
									Your quoted rates include all costs. Card Brand fees
									(~0.13-0.14%) are absorbed into your discount rate.
								</p>
								<div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
									<p className="text-sm text-gray-700">
										<span className="font-medium">Example:</span> Client pays
										exactly "2.9% + $0.30"
									</p>
								</div>
							</div>
						</label>

						{/* Pass-Through Pricing */}
						<label
							className={`flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
								configuration.pass_through_card_brand_fees ||
								configuration.pass_through_domestic_card_brand_fees ||
								configuration.pass_through_international_card_brand_fees
									? 'bg-purple-50 border-purple-500'
									: 'bg-white border-gray-200'
							}`}
						>
							<input
								type="radio"
								name="card_brand_fees"
								value="pass_through"
								checked={
									configuration.pass_through_card_brand_fees === true ||
									configuration.pass_through_domestic_card_brand_fees ===
										true ||
									configuration.pass_through_international_card_brand_fees ===
										true
								}
								onChange={() =>
									onUpdate({
										pass_through_card_brand_fees: true,
										pass_through_domestic_card_brand_fees: true,
										pass_through_international_card_brand_fees: true,
									})
								}
								className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<div className="flex items-center">
									<span className="font-semibold text-gray-900">
										Pass-Through Pricing
									</span>
								</div>
								<p className="text-gray-600 mt-2">
									Card Brand fees are added on top of your quoted rates. You
									pass card brand costs directly to the client.
								</p>

								{/* Granular selection checkboxes - show when pass-through is selected */}
								{(configuration.pass_through_card_brand_fees ||
									configuration.pass_through_domestic_card_brand_fees ||
									configuration.pass_through_international_card_brand_fees) && (
									<div className="mt-4 p-4 bg-white rounded border border-purple-200">
										<p className="text-sm font-medium text-gray-900 mb-3">
											Select which Card Brand fees to pass through:
										</p>
										<div className="space-y-3">
											{/* Domestic Card Brand Fees */}
											<label className="flex items-start cursor-pointer">
												<input
													type="checkbox"
													checked={
														configuration.pass_through_domestic_card_brand_fees ===
														true
													}
													onChange={(e) => {
														e.stopPropagation();
														const newDomestic =
															!configuration.pass_through_domestic_card_brand_fees;
														const newInternational =
															configuration.pass_through_international_card_brand_fees ??
															false;
														onUpdate({
															pass_through_domestic_card_brand_fees:
																newDomestic,
															// Update legacy field based on any being true
															pass_through_card_brand_fees:
																newDomestic || newInternational,
														});
													}}
													className="mt-0.5 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
												/>
												<div className="ml-3">
													<span className="text-sm font-medium text-gray-900">
														Domestic Card Brand Fees
													</span>
													<p className="text-xs text-gray-500">
														0.20% base rate on all transaction volume
													</p>
												</div>
											</label>

											{/* International Card Brand Fees */}
											<label className="flex items-start cursor-pointer">
												<input
													type="checkbox"
													checked={
														configuration.pass_through_international_card_brand_fees ===
														true
													}
													onChange={(e) => {
														e.stopPropagation();
														const newInternational =
															!configuration.pass_through_international_card_brand_fees;
														const newDomestic =
															configuration.pass_through_domestic_card_brand_fees ??
															false;
														onUpdate({
															pass_through_international_card_brand_fees:
																newInternational,
															// Update legacy field based on any being true
															pass_through_card_brand_fees:
																newDomestic || newInternational,
														});
													}}
													className="mt-0.5 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
												/>
												<div className="ml-3">
													<span className="text-sm font-medium text-gray-900">
														International Card Brand Fees
													</span>
													<p className="text-xs text-gray-500">
														1.45% for Visa/MC/Discover, 1% for AMEX + $0.01-0.02
														per transaction
													</p>
												</div>
											</label>
										</div>

										<div className="mt-3 text-xs text-gray-500 italic">
											International fees apply only to cards issued outside the
											US
										</div>
									</div>
								)}

								<div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
									<p className="text-sm text-gray-700">
										<span className="font-medium">Example:</span> Client pays
										"2.9% + $0.30 + Card Brand Fees"
									</p>
									<p className="text-sm text-gray-600 mt-1">
										{configuration.pass_through_international_card_brand_fees &&
										!configuration.pass_through_domestic_card_brand_fees
											? 'International CB fee is ~1.45%, so international transactions add ~$145 per $10,000 in volume'
											: configuration.pass_through_domestic_card_brand_fees &&
												  !configuration.pass_through_international_card_brand_fees
												? 'Domestic CB fee is ~0.20%, so client pays an extra ~$20 per $10,000 in volume'
												: 'Domestic: ~$20 per $10,000 | International: ~$145 per $10,000 in volume'}
									</p>
								</div>
							</div>
						</label>
					</div>
				</div>
			)}

			{/* Divider between sections when both are shown */}
			{hasMultipleSections && <div className="h-1 bg-gray-200 rounded"></div>}

			{/* IC_PLUS, TIERED: Original Assessment & Amex Configuration */}
			{(hasRateType('IC_PLUS') || hasRateType('TIERED')) && (
				<div className="rounded-lg border border-gray-200 p-6 bg-white">
					{/* Section Header - only show when multiple sections */}
					{hasMultipleSections && (
						<div className="mb-6">
							<h4 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
								<span className="w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
								{hasRateType('IC_PLUS') && hasRateType('TIERED')
									? 'Interchange-Plus & Tiered Pricing'
									: hasRateType('IC_PLUS')
										? 'Interchange-Plus Pricing'
										: 'Tiered Pricing'}
							</h4>
							<p className="text-gray-600 text-sm">
								Configure assessment rates and special Amex handling for
								wholesale pricing models
							</p>
						</div>
					)}

					{/* Question 1: Card Brand Assessment fees */}
					<div className="space-y-4">
						<h4 className="text-lg font-medium text-gray-900">
							{hasMultipleSections ? '1. ' : ''}Do you modify the standard Card
							Brand Assessment fees?
						</h4>

						{/* Pass through standard rates */}
						<label className="flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
							<input
								type="radio"
								name="assessment_rates"
								value="standard"
								checked={configuration.use_standard_assessment_rates}
								onChange={() => handleAssessmentRateChange(true)}
								className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<span className="font-normal text-gray-700">
									No, pass through the standard Assessment rates automatically.
								</span>
							</div>
						</label>

						{/* Use custom rates */}
						<label className="flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
							<input
								type="radio"
								name="assessment_rates"
								value="custom"
								checked={!configuration.use_standard_assessment_rates}
								onChange={() => handleAssessmentRateChange(false)}
								className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<span className="font-normal text-gray-700">
									Yes, I use custom or bundled Assessment rates.
								</span>

								{!configuration.use_standard_assessment_rates && (
									<div className="mt-4">
										<RangeSlider
											label="Assessment Rate"
											value={configuration.custom_assessment_rate ?? 0.14}
											onChange={handleCustomAssessmentRateChange}
											min={0}
											max={1}
											step={0.0001}
											unit="%"
											precision={4}
										/>
									</div>
								)}
							</div>
						</label>
					</div>

					{/* Question 2: American Express bundled rate */}
					<div className="space-y-4 mt-8">
						<h4 className="text-lg font-medium text-gray-900">
							{hasMultipleSections ? '2. ' : ''}Do you use a special bundled
							rate for American Express?
						</h4>

						{/* Use standard discount rate */}
						<label className="flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
							<input
								type="radio"
								name="amex_rate"
								value="standard"
								checked={!configuration.use_bundled_amex_rate}
								onChange={() => handleAmexRateChange(false)}
								className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<span className="font-normal text-gray-700">
									No, my Standard Discount Rate from Step 3 applies to Amex.
								</span>
							</div>
						</label>

						{/* Use bundled rate */}
						<label className="flex items-start p-6 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
							<input
								type="radio"
								name="amex_rate"
								value="bundled"
								checked={configuration.use_bundled_amex_rate}
								onChange={() => handleAmexRateChange(true)}
								className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
							/>
							<div className="ml-4 flex-1">
								<span className="font-normal text-gray-700">
									Yes, I use a bundled rate for American Express.
								</span>
								{configuration.use_bundled_amex_rate && (
									<div className="mt-4">
										<RangeSlider
											label="Bundled Amex Rate"
											value={configuration.bundled_amex_rate ?? 0}
											onChange={handleBundledAmexRateChange}
											min={0}
											max={5}
											step={0.0001}
											unit="%"
											precision={4}
										/>
									</div>
								)}
							</div>
						</label>
					</div>
				</div>
			)}

			{/* Navigation - Only show if not hidden */}
			{!hideNavigation && (
				<div className="flex justify-between items-center pt-8 mt-12">
					<button
						onClick={onPrevious}
						className="flex items-center text-gray-500 hover:text-gray-700 transition-colors font-medium"
					>
						<ChevronLeft className="w-5 h-5 mr-1" />
						Previous
					</button>
					<div className="flex items-center text-gray-500">
						<span className="text-sm font-medium">Step 4 of 4</span>
					</div>
					<button
						onClick={onSave}
						disabled={isLoading}
						className="flex items-center px-8 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoading ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
								{isEditing ? 'Updating...' : 'Saving...'}
							</>
						) : (
							<>
								<Check className="w-5 h-5 mr-2" />
								{isEditing ? 'Update Configuration' : 'Save Configuration'}
							</>
						)}
					</button>
				</div>
			)}
		</div>
	);
};

export default PassThroughCostsStep;
